import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MessageSquare, Phone, ChevronDown, Bot } from "lucide-react";
import { Logo, PoweredBy } from "@/components/brand/Logo";
import { NexusChat } from "@/components/NexusChat";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { label: "Services", to: "/services" },
  { label: "Nexus AI", to: "/nexus-ai" },
  { label: "Pricing", to: "/pricing" },
  { label: "Downloads", to: "/digital-downloads" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function PublicLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const location = useLocation();
  const isChatPage = location.pathname === "/chat";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 tdw-glass border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => cn("px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  isActive ? "text-electric" : "text-slate-600 hover:text-navy")}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <Link to="/owner/login"><Button variant="ghost" size="sm" data-testid="nav-owner-login">Owner Login</Button></Link>
            <Link to="/customer/login"><Button variant="outline" size="sm" data-testid="nav-customer-login">Client Login</Button></Link>
            <Link to="/chat"><Button size="sm" className="bg-electric hover:bg-tech gap-1.5" data-testid="nav-chat-cta"><Bot className="h-4 w-4" /> Chat with Nexus</Button></Link>
          </div>
          <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t bg-white px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100">{n.label}</NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              <Link to="/owner/login" className="flex-1" onClick={() => setMobileOpen(false)}><Button variant="outline" className="w-full" size="sm">Owner</Button></Link>
              <Link to="/customer/login" className="flex-1" onClick={() => setMobileOpen(false)}><Button variant="outline" className="w-full" size="sm">Client</Button></Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />

      {/* Floating Nexus chat */}
      {!isChatPage && (
        <>
          <div className={cn("fixed bottom-24 md:bottom-6 right-4 z-50 w-[92vw] max-w-[380px] rounded-2xl overflow-hidden shadow-2xl border border-slate-200 transition-all origin-bottom-right",
            chatOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none")}>
            <NexusChat active={chatOpen} onAutoClose={() => setChatOpen(false)} />
          </div>
          <button onClick={() => setChatOpen(!chatOpen)} data-testid="floating-chat-toggle"
            className="fixed bottom-6 right-4 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-electric to-tech text-white grid place-items-center shadow-xl hover:scale-105 transition-transform">
            {chatOpen ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
          </button>
        </>
      )}

      {/* Mobile sticky bar */}
      {!isChatPage && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-navy text-white flex border-t border-white/10">
          <a href="tel:+16185550142" className="flex-1 py-3 flex items-center justify-center gap-1.5 text-sm"><Phone className="h-4 w-4" /> Call</a>
          <Link to="/chat" className="flex-1 py-3 flex items-center justify-center gap-1.5 text-sm bg-electric"><Bot className="h-4 w-4" /> Nexus</Link>
          <Link to="/request-service" className="flex-1 py-3 flex items-center justify-center gap-1.5 text-sm">Request</Link>
        </div>
      )}
    </div>
  );
}

function Footer() {
  return (
    <footer className="tdw-navy-bg text-slate-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <Logo variant="wordmark-light" />
          <p className="mt-4 text-sm text-slate-400 max-w-xs">Local. Affordable. Honest Tech Help. Serving Salem, Centralia & Southern Illinois.</p>
          <div className="mt-4"><PoweredBy /></div>
        </div>
        <FooterCol title="Services" links={[["PC Support", "/services/pc-support"], ["Web Development", "/services/web-development"], ["Automation", "/services/automation"], ["Device Support", "/services/device-support"]]} />
        <FooterCol title="Company" links={[["About", "/about"], ["Portfolio", "/portfolio"], ["Blog", "/blog"], ["Contact", "/contact"]]} />
        <FooterCol title="Get Started" links={[["Chat with Nexus", "/chat"], ["Request Service", "/request-service"], ["Book Appointment", "/book"], ["Pay Invoice", "/pay-invoice"]]} />
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Thomason Digital Works. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/legal/privacy" className="hover:text-tech">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-tech">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="font-heading font-semibold text-white text-sm mb-3">{title}</h4>
      <ul className="space-y-2">
        {links.map(([label, to]) => (
          <li key={to}><Link to={to} className="text-sm text-slate-400 hover:text-tech transition-colors">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
